import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Client } from "../models/Client";
import { ClientService } from "../services/ClientService";
import { useAuth } from "./AuthContext";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { getSubdomainFromUrl } from "../utils/url";

interface Errors {
  current?: Error | null;
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  current?: boolean;
  list?: boolean;
  details?: boolean;
}

interface ClientContextProps {
  currentClient: Client | null;
  selectedClient: Client | null;
  setSelectedClient: (clientId: string) => void;
  clients: Client[];
  loading: Loadings;
  error: Errors;
  createClient: (
    clientData: Omit<
      Client,
      "_id" | "createdAt" | "updatedAt" | "createdById" | "status"
    >,
  ) => Promise<string>;
  updateClient: (
    clientId: string,
    updatedData: Partial<Omit<Client, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteClientById: (clientId: string) => Promise<void>;
}

const ClientContext = createContext<ClientContextProps | undefined>(undefined);

export const useClient = (): ClientContextProps => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error("useClient must be used within a ClientProvider");
  }
  return context;
};

export const ClientProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [currentClient, setCurrentClient] = useState<Client | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [error, setError] = useState<Errors>({ list: null, details: null });
  const [loading, setLoading] = useState<Loadings>({
    list: false,
    details: false,
  });

  useEffect(() => {
    if (!user) return;
    const tenantId = getSubdomainFromUrl(window.location.href);
    const clientsRef = collection(db, "tenants", tenantId, "clients");
    const unsubscribe = onSnapshot(
      clientsRef,
      (docSnapshot) => {
        setLoading({ ...loading, current: false });
        if (docSnapshot.empty) {
          setClients([]);
          return;
        }
        setClients(
          docSnapshot.docs.map(
            (doc) => ({ _id: doc.id, ...doc.data() }) as Client,
          ),
        );
      },
      (err) => {
        setLoading({ ...loading, current: false });
        setError({ ...error, current: new Error(err.message) });
        console.error("Failed to fetch clients:", err);
      },
    );
    return () => {
      unsubscribe();
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const tenantId = getSubdomainFromUrl(window.location.href);
    const clientRef = doc(
      collection(db, "tenants", tenantId, "clients"),
      user.uid,
    );
    const unsubscribe = onSnapshot(
      clientRef,
      (doc) => {
        setLoading({ ...loading, details: false });
        if (doc.exists()) {
          setCurrentClient({ _id: doc.id, ...doc.data() } as Client);
        } else {
          setCurrentClient(null);
        }
      },
      (err) => {
        setLoading({ ...loading, details: false });
        setError({ ...error, details: new Error(err.message) });
        console.error("Failed to listen to client changes:", err);
      },
    );
    return () => {
      unsubscribe();
    };
  }, [user]);

  const createClient = async (
    clientData: Omit<
      Client,
      "_id" | "createdAt" | "updatedAt" | "createdById" | "status"
    >,
  ) => {
    const newClientId = await ClientService.createClient({
      ...clientData,
      status: "active",
    });
    return newClientId;
  };

  const updateClient = async (
    clientId: string,
    updatedData: Partial<Omit<Client, "_id" | "createdAt" | "updatedAt">>,
  ) => {
    await ClientService.updateClient(clientId, updatedData);
  };

  const deleteClientById = async (clientId: string) => {
    await ClientService.deleteClient(clientId);
  };

  const setSelectedClientById = async (clientId: string) => {
    if (!user) return;
    if (loading.details) {
      return;
    } // Prevent multiple calls
    setLoading((prev) => ({ ...prev, details: true })); // Start loading state
    const selectedClient = clients.find((p) => p._id === clientId);
    if (selectedClient) {
      setSelectedClient(selectedClient);
    } else {
      const professional = await ClientService.getClient(clientId);
      setSelectedClient(professional);
    }
    setLoading((prev) => ({ ...prev, details: false })); // End loading state
  };

  return (
    <ClientContext.Provider
      value={{
        currentClient,
        clients,
        updateClient,
        loading,
        error,
        selectedClient,
        setSelectedClient: setSelectedClientById,
        deleteClientById,
        createClient,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};
